import Form, { IChangeEvent } from '@rjsf/core';
import { RJSFSchema, RegistryFieldsType, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { memo, useState, useEffect } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  AddButton,
  ConnectionField,
  MoveDownButton,
  MoveUpButton,
  RelationField,
  RemoveButton,
  SubmitButton,
} from '@/components/form';
import { useChainStore } from '@/context/chain-context';
import { useChainUpsertData } from '@/hooks/use-chain-upsert-data';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  EyeSlashIcon,
  MinusIcon,
  PowerIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@/hooks/use-theme';
import { useKeycloak } from '@react-keycloak/web';
import clsx from 'clsx';
import { AuthorisedFunction } from '@/utilities/authorised-function';
import { useUpdateChainStep } from '@/hooks/use-update-chain-step';
import { Controller, useForm } from 'react-hook-form';
import { Rating } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';
import * as Dialog from '@radix-ui/react-dialog';
import { ObjectPickerForm } from '@/components/object-picker-form';
import * as Popover from '@radix-ui/react-popover';
import { LinkIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Field {
  label: string;
  value: string;
}

interface ChainHeaderSection {
  type: 'header';
  fields: Field[];
  imageUrl?: string;
  fieldsV2: {
    abstractUuid?: string;
    bundleFunction?: string;
    title?: string;
    priority?: 'low' | 'medium' | 'high';
    serviceAccount?: string;
  };
}

interface ChainHandlersSection {
  type: 'abstract_handles' | 'runtime_handles';
  schema: RJSFSchema;
  uiSchema: UiSchema | null;
  formData: any;
}

export type ChainNodeSection = ChainHeaderSection | ChainHandlersSection;

const fields: RegistryFieldsType = {
  relation: RelationField,
  connection: ConnectionField,
};

interface DeleteChainStepPayload {
  chainStepUuid: string;
}

interface ToggleEnabledChainStepPayload {
  chainStepUuid: string;
  enabled: boolean;
}

interface UpdateChainStepInputs {
  title: string;
  priority: number;
  serviceAccount: string;
}

const priorityMap: Record<number, 'low' | 'medium' | 'high'> = {
  1: 'low',
  2: 'medium',
  3: 'high',
};

function ChainNode({ id: nodeId, data, ...rest }: NodeProps) {
  const updateNodeFormData = useChainStore((state) => state.updateNodeFormData);
  const deleteNode = useChainStore((state) => state.deleteNode);
  const toggleNode = useChainStore((state) => state.toggleNode);
  const toggleEnabled = useChainStore((state) => state.toggleEnabled);
  const toggleNeighbourNodes = useChainStore(
    (state) => state.toggleNeighbourNodes
  );
  const replaceEdges = useChainStore((state) => state.replaceEdges);
  const { keycloak } = useKeycloak();
  const chainUpsertDataMutation = useChainUpsertData();
  const updateChainStepMutation = useUpdateChainStep();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const container = document.querySelector('.react-flow');
    if (container) {
      setContainer(container as HTMLElement);
    }
  }, []);

  const deleteChainStepNodeMutation = useMutation({
    mutationFn: async (payload: DeleteChainStepPayload) => {
      const res = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/bundles/graph_bundle/functions/delete_chain_step`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`,
          },
          method: 'POST',
          body: payload ? JSON.stringify(payload) : '',
        }
      );

      return res.json();
    },
  });
  const { theme, toggleTheme } = useTheme('light');
  const toogleEnabledChainStepNodeMutation = useMutation({
    mutationFn: async (payload: ToggleEnabledChainStepPayload) => {
      const res = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/bundles/graph_bundle/functions/toggle_enabled_chain_step`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`,
          },
          method: 'POST',
          body: payload ? JSON.stringify(payload) : '',
        }
      );

      return res.json();
    },
  });
  const { register, handleSubmit, control, setValue } =
    useForm<UpdateChainStepInputs>();

  function handleBlur(sectionType: string) {
    const targetSection = data.sections.find(
      (section: ChainNodeSection) => section?.type === sectionType
    );

    if (!targetSection?.formData) {
      return;
    }

    const variables = {
      abstractUuid: nodeId,
      abstractData: targetSection.formData,
    };

    chainUpsertDataMutation.mutate(variables);
  }

  function handleChange(
    changeData: IChangeEvent,
    sectionType: string,
    id?: string | undefined
  ) {
    let fieldKey;

    Object.keys(changeData.idSchema).forEach((key) => {
      if (changeData.idSchema[key]?.$id === id) {
        fieldKey = key;
      }
    });

    // In case of an array field we send the updated data to the api because we
    // need to update the edges based on the returned response.
    if (fieldKey && changeData.schema.properties[fieldKey].type === 'array') {
      const variables = {
        abstractUuid: nodeId,
        abstractData: changeData.formData,
      };

      updateNodeFormData(nodeId, sectionType, changeData.formData);

      chainUpsertDataMutation.mutate(variables, {
        onSuccess: (responseData) => {
          replaceEdges(responseData.data.edges);
        },
      });
    } else {
      updateNodeFormData(nodeId, sectionType, changeData.formData);

      const targetSection = data.sections.find(
        (section: ChainNodeSection) => section?.type === sectionType
      );

      if (
        !targetSection?.formData ||
        !fieldKey ||
        targetSection.uiSchema?.[fieldKey]?.['ui:field'] !== 'relation'
      ) {
        return;
      }

      const variables = {
        abstractUuid: nodeId,
        abstractData: targetSection.formData,
      };

      chainUpsertDataMutation.mutate(variables, {
        onSuccess: (responseData) => {
          replaceEdges(responseData.data.edges);
        },
      });

      if (
        targetSection.uiSchema?.[fieldKey]?.['ui:options']?.['type'] === 'agent'
      ) {
        setValue('title', targetSection.formData?.[fieldKey]?.label);
        updateChainStepMutation.mutate({
          abstractUuid: nodeId,
          title: targetSection.formData?.[fieldKey]?.label,
        });
      }
    }
  }

  function handleToggleNode() {
    toggleNode(nodeId);
  }

  function handleToggleNeighbours() {
    toggleNeighbourNodes(nodeId);
  }

  function handleEnabledToggle() {
    if (!data) {
      return;
    }

    toogleEnabledChainStepNodeMutation.mutate(
      {
        chainStepUuid: nodeId,
        enabled: !data.enabled,
      },
      {
        onSuccess: () => toggleEnabled(nodeId),
      }
    );
  }

  function handleUpdateChainStep(data: any) {
    updateChainStepMutation.mutate({
      abstractUuid: nodeId,
      title: data.title,
      priority: priorityMap[data.priority as keyof typeof priorityMap],
      serviceAccount: data.serviceAccount,
    });
  }

  function handleDeleteClick() {
    deleteChainStepNodeMutation.mutate(
      {
        chainStepUuid: nodeId,
      },
      {
        onSuccess: () => {
          deleteNode(nodeId);
        },
      }
    );
  }

  return (
    <Dialog.Root>
      <div className="p-4 bg-white/60 shadow-lg rounded w-[300px] relative dark:bg-gray-900/60">
        <div className="absolute top-0 -right-3.5 -translate-y-1/2 flex gap-2">
          <button
            onClick={handleEnabledToggle}
            type="button"
            className="rounded-full bg-blue-200 dark:bg-blue-800 p-1 text-blue-500 dark:text-blue-300 shadow-sm hover:bg-blue-500 dark:hover:bg-blue-600 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400"
          >
            <PowerIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {!data?.parentCollapsed ? (
            <button
              onClick={handleToggleNeighbours}
              type="button"
              className="rounded-full bg-blue-200 dark:bg-blue-800 p-1 text-blue-500 dark:text-blue-300 shadow-sm hover:bg-blue-500 dark:hover:bg-blue-600 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400"
            >
              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
          <button
            onClick={handleToggleNode}
            type="button"
            className="rounded-full bg-blue-200 dark:bg-blue-800 p-1 text-blue-500 dark:text-blue-300 shadow-sm hover:bg-blue-500 dark:hover:bg-blue-600 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400"
          >
            {data?.open ? (
              <ArrowsPointingInIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {data?.readonly !== true && (
          <Dialog.Trigger className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-200 dark:bg-red-800 p-1 text-red-500 dark:text-red-300 shadow-sm hover:bg-red-500 dark:hover:bg-red-600 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:focus-visible:outline-red-400">
            <MinusIcon className="h-5 w-5" aria-hidden="true" />
          </Dialog.Trigger>
        )}

        <Handle type="target" position={Position.Top} />

        <div className="space-y-4">
          {data?.sections
            .filter((section: ChainNodeSection) =>
              !data.open ? section.type === 'header' : true
            )
            .map((section: ChainNodeSection, i: number) => {
              if (section.type === 'header') {
                return (
                  <form onBlur={handleSubmit(handleUpdateChainStep)}>
                    <div
                      key={i}
                      className={clsx(
                        'bg-gray-100 py-3 px-4 text-center rounded-sm text-sm dark:bg-gray-800 dark:text-gray-100 relative',
                        data.enabled ? '' : 'opacity-50'
                      )}
                    >
                      {section.imageUrl && (
                        <div className="w-7 h-7 object-contain m-auto bg-gray-300 dark:bg-gray-700 p-1 rounded-full mb-1 flex justify-center items-center">
                          {theme === 'dark' ? (
                            <img
                              style={{ filter: 'brightness(0) invert(1)' }}
                              src={section.imageUrl}
                              alt="Chain step icon"
                            />
                          ) : (
                            <img
                              style={{ filter: 'brightness(0)' }}
                              src={section.imageUrl}
                              alt="Chain step icon"
                            />
                          )}
                        </div>
                      )}
                      {section?.fieldsV2?.abstractUuid ? (
                        <div className="overflow-hidden text-ellipsis text-[10px]">
                          {section.fieldsV2.abstractUuid}
                        </div>
                      ) : null}
                      {section?.fieldsV2?.bundleFunction
                        ? AuthorisedFunction(['developer']) && (
                            <div className="overflow-hidden text-ellipsis">
                              {section.fieldsV2.bundleFunction}
                            </div>
                          )
                        : null}
                      {section?.fieldsV2?.title ? (
                        <input
                          key={section.fieldsV2.title}
                          className="block flex-1 w-full rounded-md border py-2 text-gray-900 shadow-sm border-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-indigo-500 sm:text-sm sm:leading-5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          type="text"
                          {...register('title', {
                            value: section.fieldsV2.title,
                          })}
                        />
                      ) : null}
                      {section?.fieldsV2?.priority ? (
                        <div className="text-indigo-500 dark:text-indigo-400 bg-white dark:bg-gray-700 ring-indigo-900/20 dark:ring-indigo-400/20 rounded-md whitespace-nowrap mt-0.5 px-1.5 py-1 text-xs font-medium ring-1 ring-inset inline-flex">
                          <Controller
                            control={control}
                            name="priority"
                            rules={{
                              validate: (priority) => priority > 0,
                            }}
                            defaultValue={
                              section.fieldsV2.priority === 'high'
                                ? 3
                                : section.fieldsV2.priority === 'medium'
                                ? 2
                                : 1
                            }
                            render={({
                              field: { onChange, onBlur, value },
                            }) => {
                              return (
                                <Rating
                                  style={{ width: '3.75rem' }}
                                  value={value}
                                  isRequired
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  items={3}
                                  itemStyles={{
                                    itemShapes: (
                                      <path
                                        fillRule="evenodd"
                                        d="M14.615 1.595a.75.75 0 0 1 .359.852L12.982 9.75h7.268a.75.75 0 0 1 .548 1.262l-10.5 11.25a.75.75 0 0 1-1.272-.71l1.992-7.302H3.75a.75.75 0 0 1-.548-1.262l10.5-11.25a.75.75 0 0 1 .913-.143Z"
                                        clipRule="evenodd"
                                      />
                                    ),
                                    activeFillColor: '#6366f1',
                                    inactiveFillColor: '#e0e7ff',
                                  }}
                                />
                              );
                            }}
                          />
                        </div>
                      ) : null}
                      {Object.hasOwn(section?.fieldsV2, 'serviceAccount') ? (
                        <Controller
                          control={control}
                          name="serviceAccount"
                          defaultValue={
                            section?.fieldsV2.serviceAccount
                          }
                          render={({ field: { value, onChange } }) => (
                            <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
                              <Popover.Portal>
                                <Popover.Content
                                  className="bg-white rounded-lg p-4 shadow-lg"
                                  sideOffset={5}
                                  side="top"
                                >
                                  <ObjectPickerForm 
                                    type="svc"
                                    label="Service Account"
                                    onSubmit={(picked) => {
                                      onChange(picked.value);
                                      setIsOpen(false);
                                    }}
                                  />
                                  <Popover.Close
                                    className="absolute top-2 right-2"
                                    aria-label="Close"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </Popover.Close>
                                  <Popover.Arrow className="fill-white" />
                                </Popover.Content>
                              </Popover.Portal>
                              <Popover.Trigger asChild>
                                <button type="button" className="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400 border-dashed justify-center text-center h-5 w-full">
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </Popover.Trigger>
                              {value && (
                                <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 bg-white py-2 pl-2 pr-3 shadow-sm text-xs">
                                  <div className="relative w-10 h-10 bg-purple-50 rounded-full flex justify-center items-center text-center">
                                    <LinkIcon className="h-5 w-5" />
                                  </div>
                                  <div className="flex-1 min-w-0 w-0 basis-1 text-left">
                                    <p className="font-medium text-gray-900">
                                      Service Account
                                    </p>
                                    <p className="truncate text-gray-500">{value}</p>
                                  </div>
                                </div>
                              )}
                            </Popover.Root>
                          )}
                        />
                      ) : null}
                    </div>
                  </form>
                );
              }

              if (section.type === 'abstract_handles') {
                // @todo Remove this once after testing
                if (section.uiSchema?.agentUuid2) {
                  section.uiSchema.agentUuid2 = {
                    'ui:field': 'relation',
                    'ui:widget': 'picker',
                    'ui:options': {
                      type: 'agent',
                      preview: false,
                    },
                  };
                }

                return !Array.isArray(section.schema) && section.schema ? (
                  <div
                    key={i}
                    className="bg-gray-100/30 p-4 space-y-1 dark:bg-gray-600/50 dark:text-gray-100"
                  >
                    <Form
                      onChange={(data, id) => {
                        handleChange(data, section.type, id);
                      }}
                      onBlur={() => handleBlur(section.type)}
                      formData={section.formData}
                      schema={section.schema}
                      uiSchema={section.uiSchema ?? {}}
                      fields={fields}
                      validator={validator}
                      templates={{
                        ButtonTemplates: {
                          AddButton,
                          MoveDownButton,
                          MoveUpButton,
                          RemoveButton,
                          SubmitButton,
                        },
                      }}
                    >
                      <div />
                    </Form>
                  </div>
                ) : null;
              }

              if (section.type === 'runtime_handles') {
                return !Array.isArray(section.schema) && section.schema ? (
                  <div
                    key={i}
                    className="bg-gray-100/30 p-4 space-y-1 dark:bg-gray-600/50 dark:text-gray-100"
                  >
                    <Form
                      onChange={(data) => {
                        updateNodeFormData(nodeId, section.type, data.formData);
                      }}
                      formData={section.formData}
                      schema={section.schema}
                      uiSchema={section.uiSchema ?? {}}
                      fields={fields}
                      validator={validator}
                      templates={{
                        ButtonTemplates: {
                          AddButton,
                          MoveDownButton,
                          MoveUpButton,
                          RemoveButton,
                          SubmitButton,
                        },
                      }}
                    >
                      <div />
                    </Form>
                  </div>
                ) : null;
              }
            })}
        </div>

        <Handle id="source" type="source" position={Position.Bottom} />
      </div>
      <Dialog.Portal container={container}>
        <Dialog.Overlay className="absolute inset-0 bg-gray-500/75 dark:bg-gray-900/80 transition-opacity z-10" />
        <Dialog.Content className="absolute inset-0 overflow-y-auto z-20">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg m-10 max-w-[500px]">
              <div className="bg-white dark:bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title asChild>
                      <h3
                        className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100"
                        id="modal-title"
                      >
                        Delete chain step
                      </h3>
                    </Dialog.Title>
                    <Dialog.Description className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this chain step? This
                      action cannot be undone.
                    </Dialog.Description>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <Dialog.Close
                  onClick={handleDeleteClick}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 dark:bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 dark:hover:bg-red-600 sm:ml-3 sm:w-auto"
                >
                  Delete
                </Dialog.Close>
                <Dialog.Close className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto">
                  Cancel
                </Dialog.Close>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

ChainNode.displayName = 'ChainNode';

export default memo(ChainNode);
